import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Flex } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import useApiClient from '../../../hooks/api/useApiClient';

function loadYousignSdk() {
  return new Promise((resolve, reject) => {
    if (window.Yousign) {
      resolve(window.Yousign);

      return;
    }

    const existing = document.querySelector('script[src="/yousign-iframe-sdk-1.6.0.min.js"]');

    if (existing) {
      existing.addEventListener('load', () => resolve(window.Yousign));
      existing.addEventListener('error', reject);

      return;
    }

    const script = document.createElement('script');
    script.src = '/yousign-iframe-sdk-1.6.0.min.js';
    script.onload = () => resolve(window.Yousign);
    script.onerror = reject;

    document.body.appendChild(script);
  });
}

/**
 * Loading the signing iframe opens the signer's own link, which Yousign reports as the
 * owner opening the contract. So the iframe only loads once the rep chooses to sign in
 * person, and the backend is told first so it can drop that link-opened notice.
 */
export default function YousignInPersonSigning({ contractId, signatureLink, onSignatureDone, onDeclined }) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Contract:SignStep' });

  const { ServiceContractsManager } = useApiClient();

  const [isSigningInPerson, setIsSigningInPerson] = useState(false);
  const [startingInPersonSigning, setStartingInPersonSigning] = useState(false);

  const startInPersonSigning = () => {
    setStartingInPersonSigning(true);

    // Signing must not depend on the notice being suppressed, so open the iframe either way.
    ServiceContractsManager.startInPersonSigning(contractId).finally(() => {
      setStartingInPersonSigning(false);
      setIsSigningInPerson(true);
    });
  };

  useEffect(() => {
    if (!isSigningInPerson || !signatureLink) {
      return;
    }

    let ys;

    loadYousignSdk().then(() => {
      ys = new window.Yousign({
        signatureLink,
        iframeContainerId: 'yousign-iframe-container',
        isSandbox: import.meta.env.VITE_APP_MODE !== 'production',
        iframeAttributes: {
          referrerPolicy: 'origin-when-cross-origin',
        },
      });

      ys.onSignatureDone(() => onSignatureDone());
      ys.onDeclined(() => onDeclined());
    });

    return () => ys?.removeMessageListener();
  }, [isSigningInPerson, signatureLink]);

  if (!isSigningInPerson) {
    return (
      <Flex vertical align="flex-start" gap="small">
        <p>{t('in_person_signing.description')}</p>
        <Button
          type="primary"
          icon={<EditOutlined />}
          loading={startingInPersonSigning}
          disabled={!signatureLink}
          onClick={startInPersonSigning}
        >
          {t('buttons.sign_in_person')}
        </Button>
      </Flex>
    );
  }

  return <div id="yousign-iframe-container" className="[&>iframe]:size-full h-screen" />;
}
